import orderBy from 'lodash/orderBy'

import createComputed from 'modules/core/hooks/createComputed'

import type { AnalyticQuestionGroup, Question } from 'modules/surveys/store/models/AnalyticResult'

import type { Data as BarsData } from './charts/Bars'
import type { Data as PieData } from './charts/Pie'

export interface Answer {
  key: string
  respondentId: string | null | undefined
  value: string | number | undefined
}

export const useAnswers = createComputed(
  ({ id }: Question, questionGroups: AnalyticQuestionGroup[]): Answer[] => {
    return questionGroups.map((questionGroup) => {
      return {
        key: questionGroup.id,
        value: questionGroup.answers[id],
        respondentId: questionGroup.participant_id,
      }
    })
  }
)
interface RatingReturn {
  data: BarsData[]
  minValue: number
  maxValue: number
}

export const useRatingData = createComputed(
  (question: Question, answers: Answer[]): RatingReturn => {
    const minValue = 0
    let maxValue = 0

    const ratingRange = (question.max_value as number) - (question.min_value as number) + 1

    const data = Array.from(
      {
        length: ratingRange,
      },
      (_, i) => ({
        // FIXME: Maybe we should accept number as a key for bar data?
        value: (i + (question.min_value as number)).toString(),
        count: 0,
      })
    )
    answers.forEach(({ value }) => {
      const index = Number(value) - (question.min_value as number)
      const item = data[index]

      if (!item) return

      item.count += 1
      maxValue = Math.max(maxValue, item.count)
    })

    return {
      data,
      minValue,
      maxValue,
    }
  }
)

export const useSingleChoicePieData = createComputed((answers: Answer[]): PieData[] => {
  const answersCounts = answers.reduce((memo: { [key: string]: number }, { value }) => {
    if (!value) return memo
    return {
      ...memo,
      [value]: (memo[value] || 0) + 1,
    }
  }, {})

  return Object.entries(answersCounts).map(([key, value]) => ({
    id: key,
    label: key,
    value: value,
  }))
})

interface SingleChoiceBarsReturn {
  data: BarsData[]
  minValue: number
  maxValue: number
}

export const useSingleChoiceBarsData = createComputed(
  (answers: Answer[]): SingleChoiceBarsReturn => {
    const minValue = 0
    let maxValue = 0

    const answersCounts = answers.reduce((memo: { [key: string]: number }, { value }) => {
      if (!value) return memo

      const newValue = (memo[value] || 0) + 1
      maxValue = Math.max(maxValue, newValue)

      return { ...memo, [value]: newValue }
    }, {})

    const answersCountsValues = Object.entries(answersCounts)

    const data = orderBy(
      answersCountsValues.map(([key, value]) => ({
        value: key,
        count: value,
      })),
      (data) => data.count,
      ['desc']
    )

    return {
      data,
      minValue,
      maxValue,
    }
  }
)
